<template>
  <!-- @focusin.stop matters whenever this popover is opened from inside a b-dropdown -
       BDXValidationReport.vue's Notifications bell (umrColumnSearchMenuRef, its own
       b-dropdown-form) is the one usage that needs it, unlike this component's original
       RecProductionReport.vue usage: bootstrap-vue's dropdown closes itself on any focusin that
       reaches document, treating it as "focus left the dropdown" - since this popover is portaled
       to <body> (see the onMounted comment below), a plain click.stop alone doesn't stop that,
       because focus/focusin fires and bubbles as its own separate event from click. Harmless for
       every other usage in this file, which has no dropdown ancestor to accidentally close. -->
  <div
    v-show="!!menu"
    ref="menuRef"
    class="fcp-th-search-menu"
    :class="{
      'fcp-th-search-menu--range': menu && 'range' === menu.type,
      'fcp-th-search-menu--date-range': menu && 'date-range' === menu.type,
      'fcp-th-search-menu--date': menu && 'date' === menu.type
    }"
    :style="menuStyle"
    @click.stop
    @focusin.stop
  >
    <!-- Blank-only mode (LIQ-2304: every column filter should have a blank/empty option) - opt-in
         per instance (allowBlank), since not every caller wants it (BDXValidationReport.vue's own
         two usages do; RecProductionReport.vue's don't, unrequested). Checking this stores
         BLANK_FILTER_VALUE (a reserved sentinel, never something a real column value or a typed
         search string could coincidentally equal) in columnSearch in place of whatever shape that
         column's own mode normally uses (a plain string for text/date, {min,max}/{from,to} for
         range/date-range) - the CALLER's own filter predicate is responsible for checking for that
         sentinel first, before falling through to its normal per-mode comparison (see
         BDXValidationReport.vue's own isAdditionalColumnFiltered/displayed*Rows for the two
         current callers). Hides the mode-specific input(s) below while active (v-if="!isBlank"),
         rather than merely disabling them, so there's no ambiguous state where a typed value is
         visible but not actually the active filter. -->
    <label v-if="allowBlank" class="fcp-th-search-blank">
      <input type="checkbox" :checked="isBlank" @change="onBlankToggle" />
      Blank only
    </label>
    <template v-if="!isBlank">
      <!-- Range mode (Total Paid (BDX)/(REC)/Variance - see RANGE_FILTER_FIELD_KEYS in
           RecProductionReport): two plain number inputs instead of the free-text box below, per
           explicit request - these are numeric amount columns, where "narrow to a substring match"
           doesn't mean much but "between X and Y" does. -->
      <template v-if="menu && 'range' === menu.type">
        <div class="fcp-th-search-range">
          <input
            type="number"
            class="fcp-th-search-input"
            placeholder="Min"
            :value="rangeMin"
            @input="onRangeInput('min', $event)"
          />
          <span class="fcp-th-search-range__sep">–</span>
          <input
            type="number"
            class="fcp-th-search-input"
            placeholder="Max"
            :value="rangeMax"
            @input="onRangeInput('max', $event)"
          />
        </div>
        <!-- Drag for a rough range, or type an exact Min/Max above - both stay in sync (see
             sliderValue's own comment). Only rendered when the bounds actually span more than one
             point - nouislider itself throws if range.min === range.max (e.g. every remaining row
             happens to share the exact same value, or nothing matches at all under every OTHER
             active filter). :key forces a full remount (BaseSlider only builds its underlying
             nouislider instance once, in its own mounted() - see its own comment) whenever either
             the open column or its own cross-filtered bounds change, since this ONE popover
             instance is reused for every range column via v-show, not recreated per column. -->
        <base-slider
          v-if="hasSliderRange"
          :key="`${menu.key}-${sliderBounds.min}-${sliderBounds.max}`"
          class="fcp-th-search-slider"
          :range="sliderBounds"
          :value="sliderValue"
          :connect="[false, true, false]"
          @input="onSliderInput"
        />
      </template>
      <!-- Date-range mode (Submit Date/Admin Date - see DATE_RANGE_FILTER_FIELD_KEYS in
           RecProductionReport): a plain From/To native date-picker pair, no slider (unlike the
           numeric range above) - a date axis has no single meaningful "drag a bar" position the
           way a bounded amount does, and native date inputs already give a familiar picker of
           their own. -->
      <div v-else-if="menu && 'date-range' === menu.type" class="fcp-th-search-range">
        <!-- change, not input: typing digit-by-digit into a native date field's mm/dd/yyyy
             segments fires input repeatedly with an EMPTY value for every segment that's still
             incomplete, so the filter would churn (and momentarily clear itself) mid-entry. change
             fires once the field holds a real, complete date - or a genuinely cleared one. -->
        <input
          type="date"
          class="fcp-th-search-input"
          :value="dateRangeFrom"
          @change="onDateRangeInput('from', $event)"
        />
        <span class="fcp-th-search-range__sep">–</span>
        <input
          type="date"
          class="fcp-th-search-input"
          :value="dateRangeTo"
          @change="onDateRangeInput('to', $event)"
        />
      </div>
      <!-- Single-date mode (BDXValidationReport.vue's Additional Claim Details table - see its own
           ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS) - a lone native date picker for a column that
           wants to filter by an actual picked date rather than a substring match typed by hand,
           unlike the plain text box below. change, not input, same reasoning as date-range above -
           and closes immediately on a pick (unlike every other mode here, which stays open across
           however many edits the user makes): a native <input type="date">'s own calendar overlay
           computes its on-screen position once, when it opens, and does NOT re-track the input if
           it's later moved via script - so if the filtering a date pick triggers reflows the
           underlying table (see reposition() below) and this popover repositions itself while that
           calendar is still showing, the small date field ends up correctly realigned but its
           calendar is left rendered at the OLD, pre-move position, visually orphaned elsewhere on
           the page (confirmed live, 2026-09). Closing immediately avoids ever repositioning out from
           under a still-open native calendar in the first place. -->
      <input
        v-else-if="menu && 'date' === menu.type"
        type="date"
        class="fcp-th-search-input"
        :value="inputValue"
        @change="onDateInput"
      />
      <input
        v-else
        type="text"
        class="fcp-th-search-input"
        placeholder="Search"
        :value="inputValue"
        @input="$emit('search', menu.key, $event.target.value)"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent, ref, computed, nextTick, onMounted, onBeforeUnmount } from '@vue/composition-api'
import BaseSlider from '@components/BaseSlider.vue'

const VIEWPORT_MARGIN = 8

// Reserved sentinel for "Blank only" mode (LIQ-2304) - stored in columnSearch[key] in place of
// that column's normal value shape when the caller opts into allowBlank and the checkbox is
// checked. Long/distinctive/all-caps-with-underscores specifically so it can never collide with
// anything a user actually types into a search box or a real column value in this data. Exported
// (not just used internally) so callers can recognize it in their own filter predicates - see this
// component's own top comment.
export const BLANK_FILTER_VALUE = '__BDX_VALIDATION_BLANK_FILTER__'

// Column-search popover behind RecProductionReport's per-column search icons (see
// HEADER_SEARCH_SORT_FIELD_KEYS there). Split out into its own component so that opening/
// closing it - a small, purely local piece of UI state - never touches RecProductionReport's
// own reactive graph: that component's Variance Transaction Details table has ~15 #cell()
// scoped slots that bootstrap-vue's b-table doesn't treat as "stable", so any reactive change
// read during RecProductionReport's own render forces a full re-render of every row/cell -
// profiled at 7-10s over ~100 rows when this popover's open/closed state lived there directly
// as a ref. Kept as its own component (rather than e.g. a composable) specifically so its state
// changes trigger only ITS OWN re-render, not the parent's.
export default defineComponent({
  name: 'rec-column-search-menu',
  components: { BaseSlider },
  props: {
    // The full { [columnKey]: text } map - not just whichever column is currently open - since
    // this component (not the parent) is the one that knows which key that is.
    columnSearch: {
      type: Object,
      default: () => ({})
    },
    // { [columnKey]: { min, max } } - range mode's own slider bounds (see RANGE_FILTER_FIELD_KEYS
    // in RecProductionReport), the actual span the currently open column's values cover across
    // every row that passes every OTHER active filter. Only meaningful for range-type columns.
    rangeBounds: {
      type: Object,
      default: () => ({})
    },
    // Opt-in for the "Blank only" checkbox (LIQ-2304) - see BLANK_FILTER_VALUE's own comment for
    // why this is per-instance rather than always-on.
    allowBlank: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    // Only one column's popover can be open at a time, so a single { key, top, left } (or null)
    // is enough - no need for one ref per column.
    const menu = ref(null)
    const menuRef = ref(null)

    // position: fixed itself lives in the .fcp-th-search-menu SCSS rule (static, always on) -
    // only the dynamic top/left need to come from here.
    const menuStyle = computed(() => (menu.value ? { top: `${menu.value.top}px`, left: `${menu.value.left}px` } : {}))

    const inputValue = computed(() => (menu.value ? props.columnSearch[menu.value.key] || '' : ''))

    const isBlank = computed(
      () => !!menu.value && BLANK_FILTER_VALUE === props.columnSearch[menu.value.key]
    )

    // Checking closes the popover (a definitive "pick", same reasoning onDateInput's own close()
    // already has - see its own comment below); unchecking just clears back to no filter and
    // leaves the popover open, so the user can immediately type/pick a real value instead.
    const onBlankToggle = (event) => {
      if (!menu.value) return
      const checked = event.target.checked
      emit('search', menu.value.key, checked ? BLANK_FILTER_VALUE : '')
      if (checked) close()
    }

    // Single-date mode's own handler (see the template's own comment on why it closes
    // immediately, unlike every other mode's input here).
    const onDateInput = (event) => {
      if (!menu.value) return
      emit('search', menu.value.key, event.target.value)
      close()
    }

    const sliderBounds = computed(() =>
      menu.value ? props.rangeBounds[menu.value.key] || { min: 0, max: 0 } : { min: 0, max: 0 }
    )

    // Whether the CALLER actually computed a real bounds entry for the open column, vs. rangeBounds
    // simply not covering this key at all (BDX Detail/Cashbook Details' own range columns - see
    // RecProductionReport.vue's downloadBdxExcel-adjacent bdxColumnSearch comment - never pass a
    // range-bounds prop at all, since their search runs over the full server-side dataset with no
    // already-loaded row sample to compute a live min/max from). Distinguishing this from
    // sliderBounds' own {min:0,max:0} DEFAULT matters below: Variance's own range columns
    // (RANGE_FILTER_FIELD_KEYS) always get a real entry here, even a degenerate {min:0,max:0} one
    // (e.g. every remaining row's amount happens to be exactly 0) - that's a genuine computed bound
    // worth previewing. BDX/Cashbook's {min:0,max:0} FALLBACK is not a bound at all, just this
    // component's own placeholder for "nothing given" - previewing THAT as if it were real made the
    // Min/Max boxes show "0"/"0" the instant a BDX/Cashbook range popover opened, before the user
    // had touched anything, reading as an already-active zero filter.
    const hasRealRangeBounds = computed(
      () => !!menu.value && Object.prototype.hasOwnProperty.call(props.rangeBounds, menu.value.key)
    )

    // Range mode's own value is { min, max } (see RANGE_FILTER_FIELD_KEYS in
    // RecProductionReport), not the plain string inputValue above reads - separate getters rather
    // than teaching inputValue to branch on shape, since a text-mode column's own value could
    // coincidentally BE an object some day and this stays unambiguous either way. Falls back to
    // the slider's own bounds (rather than a blank box) the moment the popover opens, per
    // explicit request - matches what the slider itself already shows (both handles at the
    // extremes) instead of the boxes looking unset until the user types or drags - but ONLY when
    // hasRealRangeBounds is true (see its own comment); otherwise stays blank, since there's no
    // real bound to preview. Purely a DISPLAY fallback either way: nothing is written into
    // columnSearch just from opening the popover - onRangeInput below still reads
    // props.columnSearch directly, so a filter isn't considered "active" (see
    // RecProductionReport's own isXFilterActive-style computeds elsewhere) until the user actually
    // changes something.
    const rangeMin = computed(() => {
      if (!menu.value) return ''
      const explicit = (props.columnSearch[menu.value.key] || {}).min
      if ('' !== (explicit || '')) return explicit
      return hasRealRangeBounds.value ? sliderBounds.value.min : ''
    })
    const rangeMax = computed(() => {
      if (!menu.value) return ''
      const explicit = (props.columnSearch[menu.value.key] || {}).max
      if ('' !== (explicit || '')) return explicit
      return hasRealRangeBounds.value ? sliderBounds.value.max : ''
    })

    // Merges into whatever the OTHER bound already is - typing into Min must not clobber an
    // already-typed Max (and vice versa), unlike text mode's single emit above.
    const onRangeInput = (bound, event) => {
      if (!menu.value) return
      const current = props.columnSearch[menu.value.key] || {}
      emit('search', menu.value.key, { ...current, [bound]: event.target.value })
    }

    // Date-range mode's own value is { from, to } (see DATE_RANGE_FILTER_FIELD_KEYS in
    // RecProductionReport) - plain "YYYY-MM-DD" strings straight from each <input type="date">'s
    // own value, no auto-filled default (unlike rangeMin/rangeMax above) since there's no
    // meaningful "full bounds" to show for a date column the way a numeric column has - blank
    // until the user actually picks a date, same as the plain text box.
    const dateRangeFrom = computed(() => (menu.value ? (props.columnSearch[menu.value.key] || {}).from || '' : ''))
    const dateRangeTo = computed(() => (menu.value ? (props.columnSearch[menu.value.key] || {}).to || '' : ''))

    // Merges into whatever the OTHER bound already is, same reasoning as onRangeInput above.
    const onDateRangeInput = (bound, event) => {
      if (!menu.value) return
      const current = props.columnSearch[menu.value.key] || {}
      emit('search', menu.value.key, { ...current, [bound]: event.target.value })
    }

    const hasSliderRange = computed(
      () => menu.value && 'range' === menu.value.type && sliderBounds.value.min !== sliderBounds.value.max
    )

    const clampToBounds = (value, bounds) => Math.min(Math.max(value, bounds.min), bounds.max)

    // The slider's own controlled position - falls back to the full bounds (both handles at the
    // extremes) whenever the paired text input is blank, and clamps a typed value that's outside
    // the current (cross-filtered, so it can shift as other filters change) bounds rather than
    // handing nouislider a start position outside its own declared range.
    const sliderValue = computed(() => {
      const bounds = sliderBounds.value
      const min =
        '' !== rangeMin.value && !Number.isNaN(Number(rangeMin.value))
          ? clampToBounds(Number(rangeMin.value), bounds)
          : bounds.min
      const max =
        '' !== rangeMax.value && !Number.isNaN(Number(rangeMax.value))
          ? clampToBounds(Number(rangeMax.value), bounds)
          : bounds.max

      return [min, max]
    })

    // A drag replaces BOTH bounds at once (nouislider's own "slide"/"input" payload for a
    // two-handle slider is the full [min, max] pair) - unlike onRangeInput above, which merges
    // into just the one bound a text field's own input event touched.
    const onSliderInput = (values) => {
      if (!menu.value) return
      emit('search', menu.value.key, { min: values[0], max: values[1] })
    }

    // The search icon this popover is currently anchored to, and where that icon was when the
    // position snapshot below was taken. Deliberately plain `let`s rather than refs: a DOM node
    // in reactive state gets needlessly walked by Vue's observer, and nothing renders off these -
    // only onScroll below reads them.
    let triggerEl = null
    let anchorRect = null

    const close = () => {
      menu.value = null
      triggerEl = null
      anchorRect = null
    }

    // Shared by toggle's own second pass and reposition() below - clamps a freshly-measured
    // trigger rect against the popover's own real size/the viewport, same logic both need.
    const clampToViewport = (rect, menuRect) => {
      const margin = VIEWPORT_MARGIN

      let left = rect.left
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin)

      let top = rect.bottom + 2
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2
      }
      top = Math.max(margin, top)

      return { top, left }
    }

    // Two-pass, same technique as togglePartyTypeFlyout in RecProductionReport: an initial guess
    // (right below the search icon) renders the popover so its real size can be measured, then a
    // second pass clamps it against the viewport - simpler than the DCA flyout's own version
    // since this popover never needs to flip to the icon's left, only up/down. Called directly
    // from RecProductionReport's click handler via this component's template ref, rather than the
    // parent writing to a ref of its own - see this file's own top comment for why.
    const toggle = async (key, event, type = 'text') => {
      if (menu.value && menu.value.key === key) {
        close()
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()
      triggerEl = event.currentTarget
      anchorRect = rect
      menu.value = { key, type, top: rect.bottom + 2, left: rect.left }

      await nextTick()

      if (!menuRef.value || !menu.value || menu.value.key !== key) return

      const { top, left } = clampToViewport(rect, menuRef.value.getBoundingClientRect())
      menu.value = { key, type, top, left }
    }

    // Re-measures the CURRENTLY OPEN popover's own trigger icon and re-clamps its position against
    // it - for BDXValidationReport.vue's Additional Claim Details table, whose columns are auto-
    // sized (not table-layout: fixed), so filtering the table (picking a date, typing a search)
    // can change every later column's on-screen width/position even though the popover's own
    // coordinates, snapshotted once at open time in toggle above, never move on their own. Without
    // this, the popover visually drifts away from the column it was actually opened for once its
    // own filter changes what's rendered - confirmed live 2026-09 (a date picker ending up under
    // the wrong column once fewer matching rows changed every later column's width). Deliberately
    // a reposition, not a close-on-change - unlike onScroll's own "just close it" choice, closing
    // here would fight the user picking a value, which is exactly the "filtering" gesture this
    // popover exists for. Called directly by the parent, via this component's template ref, right
    // after a filter change - this component has no way to know on its own when the parent's table
    // has finished re-rendering. anchorRect is refreshed here too, not just menu's own top/left -
    // otherwise onScroll's own movement check above would compare the NEXT scroll against the
    // pre-reposition rect and immediately treat this intentional move as an unrelated scroll,
    // closing the popover right after reposition just reopened it in the right place.
    const reposition = async () => {
      if (!menu.value || !triggerEl) return

      const { key, type } = menu.value

      await nextTick()

      if (!menuRef.value || !menu.value || menu.value.key !== key || !document.contains(triggerEl)) return

      const rect = triggerEl.getBoundingClientRect()
      anchorRect = rect

      const { top, left } = clampToViewport(rect, menuRef.value.getBoundingClientRect())
      menu.value = { key, type, top, left }
    }

    // Closes the popover on any click outside it - mousedown (not click) so it fires before the
    // toggle icon's own @click handler, matching the order a native dropdown's outside-click
    // detection would. A click on THIS popover's own trigger icon is excluded so toggle (not this
    // handler) decides whether that particular click opens, closes, or switches columns.
    //
    // Checked against triggerEl specifically, NOT a class match (`.closest('.fcp-th-search-
    // toggle')`, tried first) - reported bug: .fcp-th-search-toggle is the shared class EVERY
    // column's search icon uses (both this component's own generic popover and every b-dropdown
    // checklist column - Variance Type/Admin Approval/Account Name/UMR/Claim/Report Period/etc.),
    // so a class-based check treated a click on ANY OTHER column's icon as "my own toggle,
    // ignore it" and never closed this popover at all, even though the click plainly landed
    // somewhere else entirely.
    const onDocumentMousedown = (event) => {
      if (!menu.value) return
      if (menuRef.value && menuRef.value.contains(event.target)) return
      if (triggerEl && (triggerEl === event.target || triggerEl.contains(event.target))) return
      close()
    }

    // Scroll (capture phase, so it also catches the table's own internal overflow-y: auto
    // scrolling, not just the page) invalidates the snapshot position taken above - simplest
    // correct behaviour is to close the popover rather than track the icon live.
    //
    // ...but ONLY a scroll that actually MOVED the icon. Reported bug: picking a From date in
    // date-range mode closed the popover before a To date could be picked. Filtering from inside
    // this popover re-renders the table, which does two things that look like a scroll but
    // aren't: it shrinks the sticky-header scroll container (so the browser clamps that
    // container's scrollTop and fires a scroll event nobody asked for), and it re-renders the
    // header cells themselves (detaching the very icon this popover was anchored to). Applies
    // equally to the numeric range slider, which could lose its popover mid-drag the same way.
    const onScroll = () => {
      if (!menu.value) return

      // Focus inside the popover means the user is mid-edit - whatever caused this scroll, it
      // wasn't them scrolling, so never steal the popover out from under it.
      if (menuRef.value && menuRef.value.contains(document.activeElement)) return

      // Otherwise close only on a scroll that PROVABLY moved the trigger, invalidating the
      // position snapshot taken in toggle above. Deliberately fails open: if that can't be
      // verified (the icon was re-rendered away, so the reference is detached), leaving the
      // popover where it is beats closing one the user is still working in.
      if (!triggerEl || !anchorRect || !document.contains(triggerEl)) return

      const rect = triggerEl.getBoundingClientRect()
      if (Math.abs(rect.left - anchorRect.left) < 1 && Math.abs(rect.bottom - anchorRect.bottom) < 1) {
        return
      }

      close()
    }

    const onKeydown = (event) => {
      if ('Escape' === event.key && menu.value) close()
    }

    // Portal to <body>: this popover is triggered from inside RecProductionReport's
    // Variance Transaction Details table, whose sticky-header wrapper is a fixed-height (400px),
    // overflow-y: auto scrolling box - rendering the popover as a normal descendant there made a
    // Popper-positioned dropdown-menu compute the wrong fixed-position coordinates (fully
    // visible, but landing well below the header it's meant to sit under). Position is computed
    // manually in toggle above instead of trusting Popper. v-show (not v-if) keeps this element's
    // identity stable across opens/closes, since the portal only runs once at mount.
    onMounted(() => {
      if (menuRef.value) document.body.appendChild(menuRef.value)
      document.addEventListener('mousedown', onDocumentMousedown)
      window.addEventListener('scroll', onScroll, true)
      document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount(() => {
      if (menuRef.value && document.body.contains(menuRef.value)) {
        document.body.removeChild(menuRef.value)
      }
      document.removeEventListener('mousedown', onDocumentMousedown)
      window.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('keydown', onKeydown)
    })

    return {
      menu,
      menuRef,
      menuStyle,
      inputValue,
      isBlank,
      onBlankToggle,
      onDateInput,
      rangeMin,
      rangeMax,
      onRangeInput,
      dateRangeFrom,
      dateRangeTo,
      onDateRangeInput,
      sliderBounds,
      hasSliderRange,
      sliderValue,
      onSliderInput,
      toggle,
      reposition,
      close
    }
  }
})
</script>

<style lang="scss" scoped>
// Rendered via bootstrap-vue's b-dropdown/Popper. Restated directly here instead - top-level
// (not nested under any other selector), since this element is portaled to <body> on mount (see
// the onMounted comment above): a nested `.something .fcp-th-search-menu` selector would stop
// matching the moment it's no longer a descendant of "something", silently dropping
// position/z-index/width/background and rendering it as an unstyled, full-width, static-position
// block. z-index matches RecProductionReport's own .fcp-row-tooltip/.fcp-dca-flyout, the other
// two portaled-to-<body> elements in that file.
.fcp-th-search-menu {
  position: fixed;
  z-index: 2000;
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  padding: 0.5rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);

  // Two side-by-side number inputs need more room than the single text box above fits in.
  &--range {
    width: 190px;
    min-width: 190px;
    max-width: 190px;
  }

  // Wider still than --range above: a native <input type="date"> renders its own fixed
  // "mm/dd/yyyy" text plus a calendar-picker icon, so it has a real minimum width of its own
  // that a plain number box doesn't - two of them side by side simply don't fit in 190px.
  &--date-range {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
  }

  // Wider than the plain text box above - a native <input type="date"> renders its own fixed
  // "mm/dd/yyyy" text plus a calendar-picker icon, so it has a real minimum width of its own that
  // 150px doesn't comfortably fit.
  &--date {
    width: 170px;
    min-width: 170px;
    max-width: 170px;
  }
}

.fcp-th-search-range {
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &__sep {
    color: #adb5bd;
    flex-shrink: 0;
  }

  // Orange (#b06a1e, this rec report's own accent color) per explicit request - just these
  // Min/Max boxes, not the popover's own outer border (unchanged, the plain neutral grey every
  // other column's search popover already uses) and not .fcp-th-search-input's own base rule
  // (shared with the plain text search box below, which stays untouched).
  .fcp-th-search-input {
    border-color: #b06a1e;
  }
}

// BaseSlider renders nouislider directly onto its own root element (this class lands there via
// Vue's automatic class passthrough - see its own component), so this IS the real .noUi-target,
// not a wrapper around one. ::v-deep required here even though only margin is overridden below
// (no color override any more - reverted per explicit request, back to the app-wide slider
// theme's own default green/primary): a plain, non-::v-deep .fcp-th-search-slider rule still
// applies fine to the element itself (this component's own scope attribute lands on it via Vue's
// child-root-inherits-parent-scope behavior), so margin alone wouldn't strictly need ::v-deep -
// kept anyway since a future addition to this rule targeting .noUi-connect/.noUi-handle (nouislider's
// own injected DOM, never carrying this component's scope attribute) would otherwise silently
// stop matching again, the same bug already hit once here.
::v-deep .fcp-th-search-slider {
  // Overrides the app-wide slider theme's own `margin: 15px 0` (see
  // core/vendors/_nouislider.scss) - correct at normal dashboard-slider scale, but excessive
  // inside this small popover.
  margin: 10px 2px 4px;
}

.fcp-th-search-input {
  width: 100%;
  font-size: 0.65rem;
  border: 1px solid #c4c4c4;
  border-radius: 0.25rem;
  padding: 0.15rem 0.35rem;
  // Browser's own default focus outline reads as a heavy black ring against this small a
  // control - replaced with the same soft glow RecProductionReport's own toolbar
  // inputs/toggles use elsewhere instead.
  outline: none;

  // Chrome/Safari/Edge's native spinner arrows on type="number" - dropped per this popover's own
  // otherwise-minimal look (matches the plain text input right above it, no visible built-in
  // affordance either).
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
  appearance: textfield;
  -moz-appearance: textfield;

  &:focus {
    border-color: #1c4a50;
    box-shadow: 0 0 0 2px rgba(28, 74, 80, 0.15);
  }

  // Explicit request: Min/Max numbers always plain black, regardless of whether they're showing
  // an explicitly typed/dragged value or just the auto-filled default bounds (an earlier, grey
  // "not yet touched" treatment here was reverted per this request).
  color: #000;
}

// LIQ-2304's own "Blank only" checkbox (see allowBlank/isBlank/onBlankToggle) - sits above
// whichever mode-specific input(s) this popover is currently showing.
.fcp-th-search-blank {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.65rem;
  color: #495057;
  cursor: pointer;
  margin-bottom: 0.35rem;

  input[type='checkbox'] {
    margin: 0;
  }
}
</style>
